/* ==========================================================================
   MoldGeek — global.js
   Mobile nav, header search toggle, cart drawer + AJAX cart, quantity
   steppers, product form submission, product gallery.
   No build step / no dependencies — vanilla JS, loaded on every page.
   ========================================================================== */

(function () {
  'use strict';

  var cartDrawer = document.getElementById('CartDrawer');
  var mobileNav = document.getElementById('MobileNav');
  var searchPanel = document.getElementById('HeaderSearch');
  var cartCountEls = document.querySelectorAll('[data-cart-count]');

  /* ---------------- helpers ---------------- */

  function toggleHidden(el, show) {
    if (!el) return;
    if (show === undefined) show = el.hidden;
    el.hidden = !show;
  }

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
  }

  function updateCartCount(count) {
    cartCountEls.forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function fetchCartDrawerMarkup() {
    return fetch('/?section_id=cart-drawer')
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('CartDrawer');
        if (fresh && cartDrawer) {
          fresh.hidden = false;
          cartDrawer.replaceWith(fresh);
          cartDrawer = fresh;
          bindCartDrawerEvents();
        }
      });
  }

  /* ---------------- mobile nav ---------------- */

  function initMobileNav() {
    document.querySelectorAll('[data-mobile-menu-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var willShow = !!mobileNav.hidden;
        toggleHidden(mobileNav, willShow);
        lockScroll(willShow);
        btn.setAttribute('aria-expanded', String(willShow));
      });
    });
  }

  /* ---------------- search ---------------- */

  function initSearch() {
    document.querySelectorAll('[data-search-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var willShow = !!searchPanel.hidden;
        toggleHidden(searchPanel, willShow);
        if (willShow) {
          var input = searchPanel.querySelector('input[name="q"]');
          if (input) input.focus();
        }
      });
    });
  }

  /* ---------------- cart drawer ---------------- */

  function openCartDrawer() {
    if (!cartDrawer) return;
    toggleHidden(cartDrawer, true);
    lockScroll(true);
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    toggleHidden(cartDrawer, false);
    lockScroll(false);
  }

  function bindCartDrawerEvents() {
    if (!cartDrawer) return;

    cartDrawer.querySelectorAll('[data-cart-toggle]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        if (el.tagName === 'A') { /* let navigation happen, drawer state irrelevant after unload */ }
        closeCartDrawer();
      });
    });

    cartDrawer.querySelectorAll('[data-cart-item]').forEach(function (item) {
      var key = item.getAttribute('data-key');
      var stepper = item.querySelector('[data-quantity-stepper]');
      var removeBtn = item.querySelector('[data-cart-remove]');

      if (stepper) {
        var valueEl = stepper.querySelector('[data-quantity-value]');
        stepper.querySelector('[data-quantity-minus]').addEventListener('click', function () {
          var qty = parseInt(valueEl.textContent, 10) - 1;
          changeCartItem(key, qty);
        });
        stepper.querySelector('[data-quantity-plus]').addEventListener('click', function () {
          var qty = parseInt(valueEl.textContent, 10) + 1;
          changeCartItem(key, qty);
        });
      }

      if (removeBtn) {
        removeBtn.addEventListener('click', function () { changeCartItem(key, 0); });
      }
    });
  }

  function changeCartItem(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        return fetchCartDrawerMarkup();
      })
      .catch(function (err) { console.error('MoldGeek cart error:', err); });
  }

  function initCartToggleButtons() {
    document.querySelectorAll('[data-cart-toggle]').forEach(function (btn) {
      if (cartDrawer && cartDrawer.contains(btn)) return; // already bound in bindCartDrawerEvents
      btn.addEventListener('click', function () {
        var willShow = !!cartDrawer.hidden;
        if (willShow) { openCartDrawer(); } else { closeCartDrawer(); }
      });
    });
  }

  /* ---------------- add to cart (product forms) ---------------- */

  function initProductForms() {
    document.querySelectorAll('[data-product-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var submitBtn = form.querySelector('[data-add-to-cart]');
        var errorEl = form.querySelector('[data-form-error]');
        if (submitBtn) { submitBtn.disabled = true; submitBtn.classList.add('is-loading'); }
        if (errorEl) { errorEl.hidden = true; }

        var formData = new FormData(form);

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        })
          .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
          .then(function (result) {
            if (!result.ok) {
              throw new Error(result.data.description || 'Could not add this item to your cart.');
            }
            return fetch('/cart.js').then(function (res) { return res.json(); });
          })
          .then(function (cart) {
            updateCartCount(cart.item_count);
            return fetchCartDrawerMarkup();
          })
          .then(function () {
            openCartDrawer();
          })
          .catch(function (err) {
            if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
          })
          .finally(function () {
            if (submitBtn) { submitBtn.disabled = false; submitBtn.classList.remove('is-loading'); }
          });
      });
    });
  }

  /* ---------------- generic quantity steppers on product forms ---------------- */

  function initProductQuantitySteppers() {
    document.querySelectorAll('[data-product-form] [data-quantity-stepper]').forEach(function (stepper) {
      var input = stepper.querySelector('[data-quantity-input]');
      if (!input) return;
      stepper.querySelector('[data-quantity-minus]').addEventListener('click', function () {
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      });
      stepper.querySelector('[data-quantity-plus]').addEventListener('click', function () {
        input.value = (parseInt(input.value, 10) || 1) + 1;
      });
    });
  }

  /* ---------------- variant picker ---------------- */

  function initVariantPicker() {
    document.querySelectorAll('[data-product-form]').forEach(function (form) {
      var variantsScript = form.querySelector('[data-product-variants]');
      var optionInputs = form.querySelectorAll('.product__option-input');
      if (!variantsScript || optionInputs.length === 0) return;

      var variants;
      try { variants = JSON.parse(variantsScript.textContent); } catch (e) { return; }

      var idInput = form.querySelector('[data-variant-id]');
      var priceWrap = form.closest('.product__info') ? form.closest('.product__info').querySelector('[data-product-price]') : null;
      var addToCartBtn = form.querySelector('[data-add-to-cart]');
      var labelEl = form.querySelector('[data-add-to-cart-label]');
      var skuWrap = form.closest('.product__info') ? form.closest('.product__info').querySelector('[data-product-sku]') : null;
      var skuValue = skuWrap ? skuWrap.querySelector('[data-sku-value]') : null;

      function optionCount() {
        var count = 0;
        while (form.querySelector('input[name="option-' + count + '"]')) count++;
        return count;
      }
      var total = optionCount();

      function currentSelection() {
        var selected = [];
        for (var i = 0; i < total; i++) {
          var checked = form.querySelector('input[name="option-' + i + '"]:checked');
          selected.push(checked ? checked.value : null);
        }
        return selected;
      }

      function findVariant(selection) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var match = true;
          for (var j = 0; j < selection.length; j++) {
            if (v.options[j] !== selection[j]) { match = false; break; }
          }
          if (match) return v;
        }
        return null;
      }

      function applyVariant(variant) {
        if (!variant) {
          if (addToCartBtn) addToCartBtn.disabled = true;
          if (labelEl) labelEl.textContent = labelEl.getAttribute('data-label-unavailable');
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (priceWrap) {
          var html = '<div class="price text-mono' + (variant.compare_at_price_html ? ' price--sale' : '') + '">';
          if (variant.compare_at_price_html) {
            html += '<s class="price__compare-at">' + variant.compare_at_price_html + '</s>';
          }
          html += '<span class="price__current">' + variant.price_html + '</span></div>';
          priceWrap.innerHTML = html;
        }

        if (skuWrap && skuValue) {
          if (variant.sku) { skuValue.textContent = variant.sku; skuWrap.hidden = false; }
          else { skuWrap.hidden = true; }
        }

        if (addToCartBtn) addToCartBtn.disabled = !variant.available;
        if (labelEl) labelEl.textContent = variant.available ? labelEl.getAttribute('data-label-available') : labelEl.getAttribute('data-label-sold-out');
      }

      optionInputs.forEach(function (input) {
        input.addEventListener('change', function () {
          applyVariant(findVariant(currentSelection()));
        });
      });
    });
  }

  /* ---------------- cart page (non-drawer quick remove) ---------------- */

  function initCartPageRemove() {
    document.querySelectorAll('[data-cart-page-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-key');
        var form = btn.closest('form');
        var input = form.querySelector('input[name="updates[' + key + ']"]');
        if (input) { input.value = 0; form.submit(); }
      });
    });
  }

  /* ---------------- product gallery ---------------- */

  function initProductGallery() {
    document.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
      var mainImg = gallery.querySelector('[data-gallery-main] img');
      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          if (!mainImg) return;
          mainImg.src = thumb.getAttribute('data-full-src');
          mainImg.srcset = '';
          gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
          thumb.classList.add('is-active');
        });
      });
    });
  }

  /* ---------------- init ---------------- */

  document.addEventListener('DOMContentLoaded', function () {
    initMobileNav();
    initSearch();
    bindCartDrawerEvents();
    initCartToggleButtons();
    initProductForms();
    initProductQuantitySteppers();
    initVariantPicker();
    initProductGallery();
    initCartPageRemove();

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (cartDrawer && !cartDrawer.hidden) closeCartDrawer();
      if (mobileNav && !mobileNav.hidden) { mobileNav.hidden = true; lockScroll(false); }
      if (searchPanel && !searchPanel.hidden) searchPanel.hidden = true;
    });
  });
})();
